import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainScreenHeader from '../../common/Header/MainScreenHeader';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import ErrorMessage from '../../common/ErrorMessage/ErrorMessage';
import RoleGuard from '../../common/RoleGuard';
import { UserRoles } from '../../../constants/UserRoles';
import { CustomerInvoiceRestDataProvider } from '../../../data/CustomerInvoiceRestDataProvider';
import { CustomerRestDataProvider } from '../../../data/CustomerRestDataProvider';
import { FiscalYearRestDataProvider } from '../../../data/FiscalYearRestDataProvider';
import { VatRateRestDataProvider } from '../../../data/VatRateRestDataProvider';
import { ProductOrServiceRestDataProvider } from '../../../data/ProductOrServiceRestDataProvider';
import { ReferenceNumberConfigRestDataProvider } from '../../../data/ReferenceNumberConfigRestDataProvider';
import { CustomerInvoice } from '../../../models/CustomerInvoice';
import { Customer } from '../../../models/Customer';
import { FiscalYear } from '../../../models/FiscalYear';
import { VatRate } from '../../../models/VatRate';
import { ProductOrService } from '../../../models/ProductOrService';
import CustomerInvoiceDialog from './CustomerInvoiceDialog';
import { ToolbarFilterConfig } from '../../common/Toolbar/Toolbar';
import './CustomerInvoices.scss';
import '../../../styles/GenericSiteStyles.scss';

const CustomerInvoices: React.FC = () => {
    const { t } = useTranslation(['common', 'screens']);
    const [invoices, setInvoices] = useState<CustomerInvoice[]>([]);
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [fiscalYears, setFiscalYears] = useState<FiscalYear[]>([]);
    const [vatRates, setVatRates] = useState<VatRate[]>([]);
    const [products, setProducts] = useState<ProductOrService[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState<CustomerInvoice | null>(null);
    const [selectedFiscalYearId, setSelectedFiscalYearId] = useState<string>('');
    const [onlyUnpaid, setOnlyUnpaid] = useState(false);

    const invoiceProvider = useMemo(() => new CustomerInvoiceRestDataProvider(), []);
    const customerProvider = useMemo(() => new CustomerRestDataProvider(), []);
    const fiscalYearProvider = useMemo(() => new FiscalYearRestDataProvider(), []);
    const vatRateProvider = useMemo(() => new VatRateRestDataProvider(), []);
    const productProvider = useMemo(() => new ProductOrServiceRestDataProvider(), []);
    const refNumProvider = useMemo(() => new ReferenceNumberConfigRestDataProvider(), []);

    const [nextInvoiceNumber, setNextInvoiceNumber] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [inv, cust, fy, vr, prod] = await Promise.all([
                invoiceProvider.getAll(),
                customerProvider.getAll(),
                fiscalYearProvider.getAll(),
                vatRateProvider.getAll(),
                productProvider.getAll(),
            ]);
            setInvoices(inv);
            setCustomers(cust);
            setFiscalYears(fy);
            setVatRates(vr);
            setProducts(prod);
        } catch (e: any) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [invoiceProvider, customerProvider, fiscalYearProvider, vatRateProvider, productProvider]);

    useEffect(() => { loadData(); }, [loadData]);

    // Auto-select the most recent fiscal year once on initial load.
    const hasAutoSelected = useRef(false);
    useEffect(() => {
        if (fiscalYears.length > 0 && !hasAutoSelected.current) {
            hasAutoSelected.current = true;
            const sorted = [...fiscalYears].sort((a, b) => b.start.localeCompare(a.start));
            setSelectedFiscalYearId(sorted[0].id);
        }
    }, [fiscalYears]);

    const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);

    const filteredInvoices = useMemo(() => {
        return invoices.filter(inv => {
            if (selectedFiscalYearId && inv.fiscalYearId !== selectedFiscalYearId) return false;
            if (onlyUnpaid && inv.isPaid) return false;
            return true;
        });
    }, [invoices, selectedFiscalYearId, onlyUnpaid]);

    // ── Toolbar filter configs ────────────────────────────────────────────────
    const toolbarFilters = useMemo<ToolbarFilterConfig[]>(() => [
        {
            key: 'fiscalYear',
            type: 'select',
            label: t('common:fiscalYear'),
            value: selectedFiscalYearId,
            onChange: setSelectedFiscalYearId,
            allLabel: t('common:allFiscalYears'),
            options: [...fiscalYears]
                .sort((a, b) => b.start.localeCompare(a.start))
                .map(fy => ({ label: fy.name, value: fy.id })),
        },
        {
            key: 'onlyUnpaid',
            type: 'checkbox',
            label: t('common:unpaidOnly'),
            value: onlyUnpaid,
            onChange: setOnlyUnpaid,
        },
    ], [fiscalYears, selectedFiscalYearId, onlyUnpaid, t]);

    const peekInvoiceNumber = useCallback(async () => {
        try {
            const { formattedNumber } = await refNumProvider.peek('CINV');
            setNextInvoiceNumber(formattedNumber);
        } catch {
            setNextInvoiceNumber('');
        }
    }, [refNumProvider]);

    useEffect(() => { peekInvoiceNumber(); }, [peekInvoiceNumber]);

    const columns: DetailedListColumn<CustomerInvoice>[] = useMemo(() => [
        {
            key: 'invoiceNumber',
            header: t('screens:customerInvoices.columns.invoiceNumber'),
            dataField: 'invoiceNumber',
            type: 'string',
            width: 220,
        },
        {
            key: 'customer',
            header: t('screens:customerInvoices.columns.customer'),
            dataField: 'customerId',
            render: inv => {
                const c = customers.find(x => x.id === inv.customerId);
                return <span>{c?.name ?? inv.customerId}</span>;
            },
        },
        {
            key: 'invoiceDate',
            header: t('screens:customerInvoices.columns.invoiceDate'),
            dataField: 'invoiceDate',
            type: 'date',
            width: 160,
        },
        {
            key: 'paymentDueDate',
            header: t('screens:customerInvoices.columns.dueDate'),
            dataField: 'paymentDueDate',
            type: 'date',
            width: 160,
        },
        {
            key: 'totalAmountIncVat',
            header: t('screens:customerInvoices.columns.totalIncVat'),
            dataField: 'totalAmountIncVat',
            width: 160,
            render: inv => <span>€ {(inv.totalAmountIncVat ?? 0).toFixed(2)}</span>,
        },
        {
            key: 'status',
            header: t('screens:customerInvoices.columns.status'),
            dataField: 'isBooked',
            width: 120,
            render: (inv) => {
                if (inv.isPaid) return <span className="badge bg-success">{t('common:paid')}</span>;
                if (inv.isBooked) return <span className="badge bg-success">{t('common:booked')}</span>;
                const overdue = !!inv.paymentDueDate && new Date(inv.paymentDueDate) < today;
                return <span className={`badge ${overdue ? 'bg-danger' : 'bg-secondary'}`}>{t('common:open')}</span>;
            },
        },
    ], [customers, today, t]);

    const openEdit = (invoice: CustomerInvoice) => {
        setSelectedInvoice(invoice);
        setDialogOpen(true);
    };

    const openAdd = () => {
        setSelectedInvoice({
            id: '',
            customerId: '',
            fiscalYearId: null,
            invoiceDate: null,
            paymentDate: null,
            deliveryDate: null,
            submitDate: null,
            isPaid: false,
            paymentTermInDays: 30,
            reference: '',
            invoiceNumber: nextInvoiceNumber,
            customerReference: '',
            comments: '',
            invoiceFileAttachmentId: null,
            invoiceEmailAttachmentId: null,
            paymentDueDate: null,
            lineItems: [],
            totalAmountExVat: 0,
            totalAmountIncVat: 0,
            isBooked: false,
                bookedAt: null,
                includeTimeWritingOverview: false,
                projectId: null,
                timeWritingFrom: null,
                timeWritingTo: null,
        });
        setDialogOpen(true);
    };

    const closeDialog = () => setDialogOpen(false);

    const handleSave = async (invoice: CustomerInvoice): Promise<CustomerInvoice> => {
        try {
            if (!invoice.id) {
                const invoiceNumber = await refNumProvider.commit('CINV');
                const newId = crypto.randomUUID();
                const saved = {
                    ...invoice,
                    id: newId,
                    invoiceNumber,
                    lineItems: invoice.lineItems.map(li => ({ ...li, customerInvoiceId: newId })),
                };
                await invoiceProvider.add(saved);
                await loadData();
                peekInvoiceNumber();
                return saved;
            } else {
                await invoiceProvider.update(invoice.id, invoice);
                await loadData();
                return invoice;
            }
        } catch (e: any) {
            setError(e.message);
            throw e;
        }
    };

    const handleDelete = async (invoice: CustomerInvoice) => {
        try {
            await invoiceProvider.delete(invoice.id);
            closeDialog();
            await loadData();
        } catch (e: any) {
            setError(e.message);
        }
    };

    const handleBook = async (invoice: CustomerInvoice) => {
        try {
            await invoiceProvider.book(invoice.id);
            await loadData();
        } catch (e: any) {
            setError(e.message);
            throw e;
        }
    };

    const handleBookPayment = async (invoice: CustomerInvoice, paymentDate: string) => {
        try {
            await invoiceProvider.bookPayment(invoice.id, paymentDate);
            await loadData();
        } catch (e: any) {
            setError(e.message);
            throw e;
        }
    };

    return (
        <RoleGuard roles={`${UserRoles.Admin},${UserRoles.Manager},${UserRoles.User}`} redirectOnDeny>
            <div className="customer-invoices-page">
                <MainScreenHeader title={t('screens:customerInvoices.title')} />
                {error && <ErrorMessage message={error} />}
                <DetailedListComponent
                    dataSource={{ data: filteredInvoices, setData: setInvoices }}
                    columns={columns}
                    rowKey={inv => inv.id}
                    editRole={`${UserRoles.Admin},${UserRoles.Manager}`}
                    addRole={`${UserRoles.Admin},${UserRoles.Manager}`}
                    onEditRequest={openEdit}
                    onAddRequest={openAdd}
                    toolbarFilters={toolbarFilters}
                />
                {loading && <div className="customer-invoices-loading">{t('common:loading')}</div>}
                <CustomerInvoiceDialog
                    open={dialogOpen}
                    invoice={selectedInvoice}
                    customers={customers}
                    fiscalYears={fiscalYears}
                    vatRates={vatRates}
                    products={products}
                    onSave={handleSave}
                    onDelete={handleDelete}
                    onBook={handleBook}
                    onBookPayment={handleBookPayment}
                    onClose={closeDialog}
                />
            </div>
        </RoleGuard>
    );
};

export default CustomerInvoices;
